import React, { useEffect, useMemo } from 'react';
import type { RouteSheetPrintData } from '@/services/workOrder.service';
import { installPrintPageCss } from '@/print-engine/installPrintPageCss';
import { flattenRouteSheetOperatorPages } from '@/utils/routeSheetOperatorPages';
import './PrintableRouteSheetOperatorPages.css';
import './PrintableLineInProcessPages.css';

export type PrintableLineInProcessPagesProps = {
  data: RouteSheetPrintData;
};

const BLANK_ROWS = 18;

const LEADING_COLUMNS = ['M/C No', 'Operator', 'Operation', 'Start Time', 'End Time', 'Total Time'] as const;
const TRAILING_COLUMNS = [
  'Date',
  'Op Sign',
  'Ins. By',
  'Instrument Utilized',
  'Acceptance Criteria',
  'Verified By',
  'Reference Doc',
] as const;

function text(v: unknown): string {
  return v != null && String(v).trim() ? String(v).trim() : '';
}

/** Blank operator-filled in-process log: one landscape A4 page per WO BOQ line. */
export const PrintableLineInProcessPages: React.FC<PrintableLineInProcessPagesProps> = ({ data }) => {
  const h = data.header;
  const pages = useMemo(() => {
    const flat = flattenRouteSheetOperatorPages(data);
    return flat.length > 0
      ? flat.map((p) => ({ boq_line_no: p.boq_line_no, item_code: p.item_code }))
      : [{ boq_line_no: h.boq_line_no ?? null, item_code: h.item_code ?? null }];
  }, [data, h.boq_line_no, h.item_code]);

  const workOrderNo = text(h.work_order_number);
  const project = text(h.project_name);

  useEffect(() => installPrintPageCss({ pageSize: 'a4 landscape' }), []);

  return (
    <div className="rs-operator-root lip-root">
      {pages.map((page, idx) => {
        const lineSr = text(page.boq_line_no);
        const woLineRef = workOrderNo && lineSr ? `${workOrderNo}-${lineSr}` : '';
        const dwgNo = text(page.item_code) || text(h.item_code);

        return (
          <section key={lineSr || dwgNo || `lip-${idx}`} className="rs-operator-page lip-page" data-page={idx + 1}>
            <table className="lip-meta">
              <tbody>
                <tr>
                  <th>Work Order</th>
                  <td>{workOrderNo}</td>
                  <th>Project</th>
                  <td>{project}</td>
                </tr>
                <tr>
                  <th>WO Line Ref</th>
                  <td>{woLineRef}</td>
                  <th>Dwg No</th>
                  <td>{dwgNo}</td>
                </tr>
              </tbody>
            </table>

            <h1 className="lip-title">LINE IN-PROCESS</h1>

            <table className="lip-grid">
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '6.5%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '4%' }} />
                <col style={{ width: '4%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '6%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr>
                  {LEADING_COLUMNS.map((label) => (
                    <th key={label}>{label}</th>
                  ))}
                  <th colSpan={2}>OP Compl / Pending</th>
                  {TRAILING_COLUMNS.map((label) => (
                    <th key={label}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: BLANK_ROWS }, (_, r) => (
                  <tr key={r}>
                    {LEADING_COLUMNS.map((label) => (
                      <td key={label} />
                    ))}
                    <td className="lip-check">
                      <span className="rs-operator-check__box" aria-label="Completed" />
                    </td>
                    <td className="lip-check">
                      <span className="rs-operator-check__box" aria-label="Pending" />
                    </td>
                    {TRAILING_COLUMNS.map((label) => (
                      <td key={label} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        );
      })}
    </div>
  );
};
